// A cast drawing brought to life (4 Nov 2026). The puppet plays acts (acts.ts) on the drawing's rig and its wrapper,
// keeps the mood's loop after each one, and layers life on top the whole time: blinks (random 2 to 6 s, sometimes
// double, never while the eyes are shut), a slow breath, a glance now and then, a small fidget, and Beat's heartbeat.
// Every puppet on the page runs on the one clock (clock.ts). Reduced motion: no acts, no loops, the mood's end pose.
import { MOOD_POSE, POSES, StyleWriter, faceOf, mixFace, partsOf, writeRig, type Face, type FrontArms, type RigParts } from './rig';
import { ACTS, REST, defaultAct, ease, endPose, mixArms, moodLoop, onTop, sampleAct, type Act, type ActName, type Arms, type WT } from './acts';
import { clockNow, onFrame } from './clock';
import type { CastMood } from './CastMascot';

export interface PuppetInit {
  svg: SVGSVGElement;
  /** the HTML box that carries the act's moves */
  wrap: HTMLElement;
  /** a friend's hand for the high five, and the burst's sparks, both inside the wrap */
  hand?: HTMLElement | null;
  burst?: HTMLElement | null;
  /** the ground shadow, outside the wrap: it stays on the ground and shrinks as the character leaves it */
  shadow?: HTMLElement | null;
  size: number;
  /** Beat's heartbeat */
  beat?: boolean;
  reduced?: boolean;
  /** life's randomness (tests pass a fixed one) */
  rand?: () => number;
}

/** How long a new act takes to take over from the face and arms the character was showing. */
const CARRY_MS = 160;
/** Life alone (no act running) redraws at most this often. */
const LIFE_FRAME_MS = 32;
const BLINK = { close: 70, open: 110, gap: 80 };

/** Beat's lub-dub (MedSnapp's website rigLubDub): scale keyframes over 1.4 s. */
const LUBDUB: ReadonlyArray<[number, number, number]> = [[0, 1, 1], [0.08, 1.045, 0.965], [0.16, 0.985, 1.02], [0.24, 1.025, 0.98], [0.4, 1, 1], [1, 1, 1]];
export function lubDub(ms: number): [number, number] {
  const p = (ms % 1400) / 1400;
  for (let i = 1; i < LUBDUB.length; i++) {
    const [t1, x1, y1] = LUBDUB[i];
    if (p <= t1) {
      const [t0, x0, y0] = LUBDUB[i - 1];
      const k = ease('io', (p - t0) / (t1 - t0));
      return [x0 + (x1 - x0) * k, y0 + (y1 - y0) * k];
    }
  }
  return [1, 1];
}

/** A blink's closure (0 open .. 1 shut) at ms after it starts; a double blink shuts twice. */
export function blinkAt(ms: number, double: boolean): number {
  const one = (t: number) => (t < 0 ? 0 : t < BLINK.close ? ease('in', t / BLINK.close) : t < BLINK.close + BLINK.open ? 1 - ease('out', (t - BLINK.close) / BLINK.open) : 0);
  return Math.max(one(ms), double ? one(ms - BLINK.close - BLINK.open - BLINK.gap) : 0);
}

const eyesShut = (f: Face) => (f.lid[0] >= 0.9 && f.lid[2] >= 0.9) || f.pupil[3] < 0.5;
const r2 = (n: number) => Math.round(n * 100) / 100;

/** The wrapper's transform: squash keeps the feet on the ground. */
export function wrapTransform(w: WT, size: number): string {
  const sx = w.s * w.sx; const sy = w.s * w.sy;
  if (w.x === 0 && w.y === 0 && w.r === 0 && sx === 1 && sy === 1) return 'none';
  const ty = w.y * size + (1 - Math.abs(sy)) * size * 0.5;
  return `translate(${r2(w.x * size)}px, ${r2(ty)}px) rotate(${r2(w.r)}deg) scale(${r2(sx * 1000) / 1000}, ${r2(sy * 1000) / 1000})`;
}

interface Shown { face: Face; arms: Arms; w: WT }

export class Puppet {
  private readonly parts: RigParts;
  private readonly w = new StyleWriter();
  private readonly front: FrontArms;
  private readonly sparks: HTMLElement[];
  private readonly rand: () => number;
  private off?: () => void;

  private mood: CastMood = 'idle';
  private act?: Act;
  private actAt = 0;
  private carry?: Shown & { at: number };
  /** where the last act left the character; the loop plays on top */
  private settled: Shown;
  private loopAt = 0;
  private shown: Shown;
  private lastWrite = -Infinity;

  private blinkStart = -Infinity; private blinkDouble = false; private nextBlink: number;
  private glanceStart = -Infinity; private glanceDir = 1; private glanceHold = 800; private nextGlance: number;
  private fidgetStart = -Infinity; private fidgetArm = 0; private nextFidget: number;
  private readonly breathPhase: number;

  constructor(parts: RigParts, private readonly init: PuppetInit) {
    this.parts = parts;
    this.rand = init.rand ?? Math.random;
    // Copies of the arms drawn after the face, shown only while an act puts a hand in front of it.
    this.front = parts.arms.map((arm) => {
      const copy = arm.cloneNode(true) as Element;
      copy.setAttribute('data-front-arm', '');
      (copy as SVGElement).style.opacity = '0';
      parts.root.appendChild(copy);
      return copy;
    }) as unknown as FrontArms;
    // Extras pop and spin about their own centres.
    for (const els of Object.values(parts.extras)) for (const el of els) {
      for (const e of [el, ...el.children]) if (e instanceof SVGElement) { e.style.transformBox = 'fill-box'; e.style.transformOrigin = 'center'; }
    }
    // The drawing's own shadow would fly with the character; the box's shadow stays on the ground instead.
    if (init.shadow) this.w.op(parts.ground, 0);
    this.sparks = init.burst ? [...init.burst.children].filter((c): c is HTMLElement => c instanceof HTMLElement || c instanceof SVGElement) as HTMLElement[] : [];
    const neutral: Shown = { face: faceOf('neutral'), arms: POSES.neutral.arms, w: { ...REST } };
    this.shown = neutral; this.settled = neutral;
    const now = clockNow();
    this.nextBlink = now + 1200 + this.rand() * 3000;
    this.nextGlance = now + 2500 + this.rand() * 4000;
    this.nextFidget = now + 5000 + this.rand() * 6000;
    this.breathPhase = this.rand() * 3400;
  }

  /** Plays `act` (or none) in `mood`, after `delay` ms; the mood's loop and life follow. */
  play(mood: CastMood, act: ActName | null | undefined, delay = 0): void {
    this.mood = mood;
    const name = act === undefined ? defaultAct(mood, '') : act;
    if (this.init.reduced) { this.still(); return; }
    const now = clockNow();
    this.carry = { ...this.shown, at: now + delay };
    if (name) {
      this.act = ACTS[name];
      this.actAt = now + delay;
    } else {
      this.act = undefined;
      this.settled = { face: faceOf(MOOD_POSE[mood]), arms: POSES[MOOD_POSE[mood]].arms, w: { ...REST } };
      this.loopAt = now;
    }
    this.lastWrite = -Infinity;
    this.frame(now);
    this.off ??= onFrame((t) => this.frame(t));
  }

  /** The act playing now, if any (tests, the gallery). */
  get playing(): string | undefined { return this.act?.name; }

  destroy(): void {
    this.off?.(); this.off = undefined;
    for (const c of this.front) c?.remove();
  }

  /** Reduced motion: the mood's end pose, nothing moving. */
  private still() {
    this.off?.(); this.off = undefined;
    this.act = undefined;
    const pose = MOOD_POSE[this.mood];
    writeRig(this.parts, { face: faceOf(pose), arms: POSES[pose].arms }, this.w, this.front);
    this.w.tf(this.init.wrap, 'none'); this.w.op(this.init.wrap, 1);
    this.w.tf(this.init.shadow, 'none'); this.w.op(this.init.shadow, 1);
    this.w.op(this.init.hand, 0); this.w.op(this.init.burst, 0);
  }

  private frame(now: number) {
    let w: WT; let arms: Arms; let face: Face; let front: readonly [number, number] = [0, 0]; let hand = 0; let burst = 0;
    const a = this.act;
    const t = now - this.actAt;
    const acting = !!a && t < a.ms;
    if (!acting && now - this.lastWrite < LIFE_FRAME_MS) return;

    if (a && acting) {
      const s = sampleAct(a, t);
      ({ w, arms, face, front, hand, burst } = s);
      const c = this.carry;
      // The new act takes over from the face and arms on show, unless it opens unseen (falling in from above).
      if (c && t >= 0 && now - c.at < CARRY_MS && c.at === this.actAt && sampleAct(a, 0).w.o > 0) {
        const k = ease('out', (now - c.at) / CARRY_MS);
        face = mixFace(c.face, face, k); arms = mixArms(c.arms, arms, k);
      }
    } else {
      if (a) {
        // The act is over: it leaves the character where its last keys put it, and the loop starts from there.
        const end = sampleAct(a, a.ms);
        this.settled = { face: faceOf(endPose(a)), arms: end.arms, w: end.w };
        this.loopAt = this.actAt + a.ms;
        this.act = undefined; this.carry = undefined;
      }
      const s = this.settled;
      w = onTop(s.w, moodLoop(this.mood, now - this.loopAt));
      face = s.face; arms = s.arms;
      const c = this.carry;
      if (c && now - c.at < 260) {
        const k = ease('out', (now - c.at) / 260);
        face = mixFace(c.face, face, k); arms = mixArms(c.arms, arms, k);
        w = { x: c.w.x + (w.x - c.w.x) * k, y: c.w.y + (w.y - c.w.y) * k, r: c.w.r + (w.r - c.w.r) * k, s: c.w.s + (w.s - c.w.s) * k, sx: c.w.sx + (w.sx - c.w.sx) * k, sy: c.w.sy + (w.sy - c.w.sy) * k, o: c.w.o + (w.o - c.w.o) * k };
      }
    }
    this.shown = { face, arms, w };

    // ---- life ----
    const shut = eyesShut(face);
    if (now >= this.nextBlink) {
      if (!shut && now - this.blinkStart > 600) { this.blinkStart = now; this.blinkDouble = this.rand() < 0.18; }
      this.nextBlink = now + 2000 + this.rand() * 4000;
    }
    const blink = shut ? 0 : blinkAt(now - this.blinkStart, this.blinkDouble);
    if (now >= this.nextGlance) {
      if (!acting && !shut) { this.glanceStart = now; this.glanceDir = this.rand() < 0.5 ? -1 : 1; this.glanceHold = 600 + this.rand() * 600; }
      this.nextGlance = now + 3500 + this.rand() * 5000;
    }
    const g = now - this.glanceStart;
    const glance = g < 0 ? 0 : g < 140 ? ease('out', g / 140) : g < 140 + this.glanceHold ? 1 : g < 320 + this.glanceHold ? 1 - ease('io', (g - 140 - this.glanceHold) / 180) : 0;
    if (now >= this.nextFidget) {
      if (!acting && !shut) { this.fidgetStart = now; this.fidgetArm = this.rand() < 0.5 ? 0 : 1; }
      this.nextFidget = now + 6000 + this.rand() * 7000;
    }
    const fg = now - this.fidgetStart;
    const fidget = fg < 0 || fg > 560 ? 0 : fg < 180 ? ease('out', fg / 180) : 1 - ease('back', (fg - 180) / 380);
    if (fidget !== 0) arms = this.fidgetArm === 0 ? [arms[0] + 24 * fidget, arms[1]] : [arms[0], arms[1] - 24 * fidget];
    const breath = Math.sin(((now + this.breathPhase) / 3400) * Math.PI * 2);

    writeRig(this.parts, {
      face, arms, front, blink,
      glance: [this.glanceDir * 4.5 * glance, 0.8 * glance],
      breathe: [1 - 0.012 * breath, 1 + 0.018 * breath],
      beat: this.init.beat ? lubDub(now) : undefined,
      t: now,
    }, this.w, this.front);

    const { size, wrap } = this.init;
    this.w.tf(wrap, wrapTransform(w, size));
    this.w.op(wrap, Math.min(1, Math.max(0, w.o)));
    if (this.init.shadow) {
      const up = Math.max(0, -w.y);
      const k = Math.max(0.25, 1 - 0.55 * up) * Math.min(1.25, Math.abs(w.s * w.sx));
      this.w.tf(this.init.shadow, `translateX(${r2(w.x * size)}px) scale(${r2(k)}, 1)`);
      this.w.op(this.init.shadow, Math.max(0, Math.min(1, w.o)) * Math.max(0.2, 1 - 0.6 * up));
    }
    if (this.init.hand) {
      this.w.tf(this.init.hand, `translate(${r2((1 - hand) * 0.8 * size)}px, 0px) rotate(-14deg)`);
      this.w.op(this.init.hand, Math.min(1, hand * 4));
    }
    if (this.init.burst) {
      const live = burst > 0 && burst < 1;
      this.w.op(this.init.burst, live ? 1 : 0);
      if (live && a?.burstAt) { this.init.burst.style.left = `${a.burstAt[0] * 100}%`; this.init.burst.style.top = `${a.burstAt[1] * 100}%`; }
      if (live) this.sparks.forEach((el, i) => {
        const th = (i / this.sparks.length) * Math.PI * 2 + 0.35;
        const r = size * (0.1 + 0.36 * ease('out', burst));
        this.w.tf(el, `translate(${r2(Math.cos(th) * r)}px, ${r2(Math.sin(th) * r)}px) scale(${r2(1.2 - 0.7 * burst)}) rotate(${Math.round(burst * 120)}deg)`);
        this.w.op(el, burst < 0.15 ? burst / 0.15 : 1 - (burst - 0.15) / 0.85);
      });
    }
    this.lastWrite = now;
  }
}

/** A puppet for a drawing in the page, or undefined if the drawing does not have the rig's shape. */
export function attachPuppet(init: PuppetInit): Puppet | undefined {
  const parts = partsOf(init.svg);
  return parts ? new Puppet(parts, init) : undefined;
}
